import {Box, Hidden} from '@mui/material';
import Grid from '@mui/material/Grid';
import AppAsyncExport from 'main/core/AppFormComponents/AppAsyncExport';
import AppsColumnSetting from 'main/core/AppsColumnSetting';
import AppSearch from 'main/core/AppSearchBar';
import AppsPagination from 'main/core/AppsPagination';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {UiWiseActionType} from 'shared/constants/UiWiseConst';
import {TableColumnVisibility} from 'shared/constants/UiWisePropType';
import WidgetAction from '../../WidgetAction';
import WidgetTitle from '../../WidgetTitle';

const TableNavBar = (props) => {
  const {
    widgetSpec,
    tableParams,
    params,
    rows = [],
    totalRows = 0,
    checkedRows = [],
    showDialog,
    dataColumns,
    onColumnVisibilityChange,
    onSelectAllColumnsVisibility,
    onSearchTextChange,
    onPageChange,
    disabled,
  } = props;

  const {messages} = useIntl();
  const {search, page, pageSize} = tableParams;
  const {
    widgetid,
    widgettitle,
    actions,
    showsearch,
    searchplaceholder,
    showexport,
    showpagination,
    changeColumnSetting,
  } = widgetSpec;

  return (
    <Box
      display='flex'
      flexDirection='row'
      alignItems='center'
      width={1}
      justifyContent='space-between'
    >
      <Box display='flex' alignItems='center' sx={{pl: 3}}>
        {widgettitle && <WidgetTitle title={widgettitle} />}
      </Box>

      <Box display='flex' alignItems='center' justifyContent='flex-end'>
        {showsearch && (
          <AppSearch
            iconPosition='right'
            overlap={false}
            value={search}
            onChange={onSearchTextChange}
            placeholder={searchplaceholder || messages['common.searchHere']}
          />
        )}

        {showexport && (
          <AppAsyncExport
            widgetid={widgetid}
            tableParams={tableParams}
            disabled={disabled}
            dataColumns={dataColumns.filter((col) => col.name !== 'thumbnail')}
          />
        )}

        <Box display='flex' alignItems='center'>
          {actions?.map((action, i) => {
            const isDisabled =
              (action.requireitemselection && checkedRows?.length === 0) ||
              disabled;

            const clickHandler =
              action.requireitemselection &&
              action.actiontype === UiWiseActionType.CONFIRM
                ? () => showDialog(action.alertref)
                : undefined;

            return (
              <Grid
                item
                key={i}
                xs='auto' // Forces the item to only take the width of its content
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '0px !important', // Sometimes Grid spacing adds top padding you don't want
                  paddingBottom: '0px !important',
                }}
              >
                <WidgetAction
                  action={action}
                  data={params}
                  disabled={isDisabled}
                  onClick={clickHandler}
                />
              </Grid>
            );
          })}

          {showpagination && rows.length > 0 && (
            <Hidden smDown>
              <AppsPagination
                pageSize={pageSize}
                totalRows={totalRows}
                page={page}
                onPageChange={onPageChange}
              />
            </Hidden>
          )}
        </Box>

        {changeColumnSetting && (
          <AppsColumnSetting
            dataColumns={dataColumns}
            onColumnVisibilityChange={onColumnVisibilityChange}
            onSelectAllColumnsVisibility={onSelectAllColumnsVisibility}
          />
        )}
      </Box>
    </Box>
  );
};

export default TableNavBar;

TableNavBar.propTypes = {
  widgetSpec: PropTypes.object,

  tableParams: PropTypes.shape({
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    sortOrder: PropTypes.oneOf(['asc', 'desc']).isRequired,
    sortColumn: PropTypes.string.isRequired,
    search: PropTypes.string,
  }).isRequired,

  params: PropTypes.object,

  // Data & UI State
  rows: PropTypes.array,
  totalRows: PropTypes.number,
  checkedRows: PropTypes.array,
  showDialog: PropTypes.func,

  // Column Management
  dataColumns: PropTypes.arrayOf(TableColumnVisibility),
  onColumnVisibilityChange: PropTypes.func,
  onSelectAllColumnsVisibility: PropTypes.func,
  onSearchTextChange: PropTypes.func,
  onPageChange: PropTypes.func,

  disabled: PropTypes.bool,
};
